import type {
  MaterializedMenuProduct,
  StoreSpecial as V3StoreSpecial,
} from '@iheartjane/data-access';
import { ALL_STORE_SPECIALS_OPTS } from '@iheartjane/data-access';
import {
  useBrandSpecialQualifyingStatus,
  useGetSpecialQualifyingStatus,
  useStoreSpecials,
} from '@iheartjane/data-access/react-query';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import {
  getPriceIdsForBrandSpecial,
  getPriceIdsForStoreSpecial,
  useNavigateBack,
} from '@iheartjane/menu';
import { Button, Flex, Typography } from '@iheartjane/reefer';
import { isProductKind } from '@iheartjane/types';
import { pluralize } from '@iheartjane/utils';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation, useMatches, useParams } from 'react-router';

import { useRecommendedSort } from '@jane/dm/internal';
import {
  FilterSection,
  Modal,
  ModalHeader,
  getColumnCount,
} from '@jane/kiosk/components';
import type { RootLoaderType } from '@jane/kiosk/data-access';
import { searchOptions, useLocalCartUuid } from '@jane/kiosk/data-access';
import {
  useKioskSpacing,
  useRowContext,
  useShouldKioskShowGold,
  useTypedRouteLoaderData,
} from '@jane/kiosk/hooks';
import {
  useBrandSpecials,
  useFormattedFilterGroups,
  useGoldFacetsAndFilters,
  useProductSort,
  useSearchContext,
} from '@jane/kiosk/providers';
import type { OptionalMenuRouteParams } from '@jane/kiosk/routing/types';
import {
  getSource,
  isSupportedStoreSpecial,
  trackFilteredProductList,
} from '@jane/kiosk/util';
import type {
  ArrayFilterValue,
  JaneSearchState,
  ProductFilterKeys,
  RangeFilterValue,
} from '@jane/search/data-access';
import {
  AVAILABLE_WEIGHTS,
  buildSearchFilter,
  handleFilterChange,
  handleFilterDeselect,
  updateFacetCounts,
} from '@jane/search/util';
import { useGetStoreSpecials } from '@jane/shared-ecomm/data-access';
import { useGetStore } from '@jane/shared/data-access';
import type { Store, StoreSpecial } from '@jane/shared/types';

export const Filters = () => {
  const showGold = useShouldKioskShowGold();

  const { store: initialStore, storeId } =
    useTypedRouteLoaderData<RootLoaderType>('root');
  const { data: store } = useGetStore<Store>(storeId, {
    initialData: initialStore,
  });
  const cartUuid = useLocalCartUuid();

  const { category, specialId, brandSpecialId } =
    useParams<OptionalMenuRouteParams>();
  const currentCategoryPage = isProductKind(category) ? category : null;

  const goBack = useNavigateBack(`stores/${storeId}/menu`);
  const { getKioskSpacing, isIpad } = useKioskSpacing();

  const { resetSearchState, searchState, setSearchState } = useSearchContext();
  const { brandSpecials, brandSpecialFilter } = useBrandSpecials();
  const [initialSearchState] =
    useState<JaneSearchState<MaterializedMenuProduct>>(searchState);

  const useV3 = useFlag(FLAGS.c3UseV3StoreSpecials);
  const multipleConditions = useFlag(FLAGS.c3MultipleConditionSpecials);
  const { data: v1Specials = [] } = useGetStoreSpecials(storeId, {
    enabled: !useV3 && !!storeId,
  });
  const v3SpecialsQuery = useStoreSpecials({
    ...ALL_STORE_SPECIALS_OPTS,
    enabled: !!useV3 && !!storeId,
    storeId,
  });
  const specials = useMemo<(StoreSpecial | V3StoreSpecial)[]>(() => {
    if (useV3) {
      return v3SpecialsQuery.data?.pages.flatMap((page) => page.specials) ?? [];
    }
    return v1Specials;
  }, [useV3, v3SpecialsQuery.data, v1Specials]);
  const supportedStoreSpecials = specials.filter((special) =>
    isSupportedStoreSpecial(special)
  );
  const specialInScope = specials.find(
    (special) =>
      `${special.id}` === specialId && isSupportedStoreSpecial(special, true)
  );
  const brandSpecial = brandSpecials.find(
    ({ id }) => `${id}` === brandSpecialId
  );

  const { data: qualifyingStatusData, refetch: refetchQualifyingStatus } =
    useBrandSpecialQualifyingStatus({
      cartUuid: cartUuid ?? undefined,
      specialId: brandSpecial?.id,
      storeId: store?.id,
    });

  const { data: storeSpecialQualifyingStatusData } =
    useGetSpecialQualifyingStatus({
      cartUuid: cartUuid ?? undefined,
      specialId: specialInScope?.id,
      storeId: store?.id,
    });

  const applicablePriceIds = specialInScope
    ? getPriceIdsForStoreSpecial({
        multipleConditions,
        qualifyingStatus:
          storeSpecialQualifyingStatusData?.qualifying_status ??
          'not_qualified',
        special: specialInScope,
      })
    : getPriceIdsForBrandSpecial(
        brandSpecial,
        qualifyingStatusData?.qualifying_status ?? 'not_qualified'
      );

  // if there are no weight restrictions, we return all of the weights
  const visiblePriceIds =
    applicablePriceIds.length > 0 &&
    applicablePriceIds.length !== AVAILABLE_WEIGHTS.length
      ? applicablePriceIds
      : null;

  const { featuredCategory, featuredFilter, rowId } = useRowContext(store);

  const isGold = category === 'jane-gold';

  const staticSearchOptions = searchOptions({
    categories: [category],
    customRowFilter: isGold ? brandSpecialFilter : featuredFilter,
    storeId,
    storeSpecial: specialInScope,
    brandSpecial,
  });
  const searchFilter = buildSearchFilter({
    searchState,
    staticFilters: staticSearchOptions.staticFilters,
  });
  const storeSearchOptions = searchOptions({
    storeId,
  });
  const { searchSort } = useProductSort({
    defaultSortName: null,
    store,
  });

  const numColumns = getColumnCount();

  /**
   * Step 1: Assembling filter options:
   * Fetch facets from search using only *static* filters.
   * This allows us to draw all possibilities (eg. all product Kinds), and establishes a baseline of whether the
   *  current page can ever allow filtering by such possibilities -- "Should the Vape product Kind always or sometimes
   *  be disabled, on a featured row or special that excludes Vapes?"
   */
  const { searchResultFacets: initialFacets } = useRecommendedSort({
    maxProducts: 0,
    numColumns,
    searchAttributes: ['*'],
    searchFilter: storeSearchOptions.staticFilters,
    searchSort,
    storeId,
  });

  /**
   * Step 2: Apply non-static filters and zip these results with the previously calculated initial facets.
   *
   * Gold facets need to be updated separately due to user segmentation making the
   * count more complicated to calculate.
   */
  const { goldFacets, goldFacetsSearchFilters } = useGoldFacetsAndFilters({
    goldFacetsEnabled:
      showGold && Boolean(initialFacets?.['has_brand_discount']?.['true']),
    staticFilters: staticSearchOptions.staticFilters,
  });

  const goldFilterValues = searchState.filters?.['has_brand_discount'] ?? [];
  const isFilteredByGold = goldFilterValues?.length > 0;

  const {
    searchResultFacets,
    numHits: filteredProductCount,
    isLoading,
  } = useRecommendedSort({
    maxProducts: 0,
    numColumns,
    searchAttributes: ['*'],
    searchFilter: isFilteredByGold ? goldFacetsSearchFilters : searchFilter,
    searchQuery: searchState.searchText ?? '',
    searchSort,
    storeId,
  });

  /**
   * Ensure filtered facets have finished fetching before updating counts.
   * This keeps all filter groups visible between renders.
   */

  const facets = searchResultFacets
    ? updateFacetCounts(
        searchState,
        { ...initialFacets, ...goldFacets },
        searchResultFacets
      )
    : initialFacets;

  const filterGroups = useFormattedFilterGroups(
    facets,
    store,
    {
      canFilterByCategory: !currentCategoryPage,
      canFilterByJaneGold: showGold && brandSpecials?.length > 0,
      canFilterByRating: store.product_reviews_enabled,
      canFilterBySpecial: !featuredCategory && !rowId && !specialInScope,
      canFilterBySubcategoriesOf: currentCategoryPage,
      visiblePriceIds: visiblePriceIds ?? undefined,
    },
    supportedStoreSpecials
  );

  /**
   * Though similar to the one that includes more of searchState, this search is used to inform Mixpanel of the total number of results
   * _before_ the customer begins to filter. This is a general search that applies to multiple menu screens -
   * All products, Category, Featured, Special, Search Results.
   */
  const { numHits: totalProductCount } = useRecommendedSort({
    maxProducts: 0,
    numColumns,
    searchAttributes: ['*'],
    searchFilter: storeSearchOptions.staticFilters,
    searchQuery: searchState.searchText ?? '',
    searchSort,
    storeId,
  });

  const discardSelection = useCallback(() => {
    setSearchState(initialSearchState);
    goBack();
  }, [initialSearchState, goBack, setSearchState]);

  const clearAllFilters = useCallback(
    () => resetSearchState({ searchText: searchState.searchText }),
    [resetSearchState, searchState]
  );

  const onChange = useCallback(
    (
      filterKey: ProductFilterKeys,
      filterValue: ArrayFilterValue | RangeFilterValue
    ) => {
      return setSearchState(
        handleFilterChange(filterKey, filterValue, searchState)
      );
    },
    [searchState, setSearchState]
  );

  const onDeselect = useCallback(
    (filterKey: ProductFilterKeys) => {
      return setSearchState(handleFilterDeselect(filterKey, searchState));
    },
    [searchState, setSearchState]
  );
  const { pathname } = useLocation();
  const matches = useMatches();

  const onShowFilteredProducts = useCallback(() => {
    trackFilteredProductList({
      dealName: specialInScope ? [specialInScope.title] : null,
      filterOptions: searchState,
      filteredProductCount,
      source: getSource({
        currentSpecial: specialInScope,
        isTopper: false,
        matches,
        pathname,
      }),
      totalProductCount: totalProductCount ?? store.product_count,
    });
  }, [
    filteredProductCount,
    totalProductCount,
    matches,
    pathname,
    searchState,
    specialInScope,
    store.product_count,
    specialInScope,
  ]);

  useEffect(() => {
    if (brandSpecialId && brandSpecial && cartUuid) {
      refetchQualifyingStatus();
    }
  }, []);

  return (
    <Modal
      appId="root"
      onRequestClose={discardSelection}
      open
      overflow="hidden"
      variant="standard"
      overlayClose
    >
      <ModalHeader onRequestClose={discardSelection}>
        <Flex alignItems="center" grow={1} justifyContent="space-between">
          <Typography mr={96} variant={isIpad ? 'h3-semibold' : 'h2-semibold'}>
            All filters
          </Typography>
          <Flex>
            <Button
              label={<Typography variant="body-semibold">Clear all</Typography>}
              onClick={clearAllFilters}
              size={isIpad ? 'small' : 'default'}
              variant="tertiary"
            />
            <Button
              disabled={filteredProductCount === 0}
              loading={isLoading}
              ml={getKioskSpacing(24, 16)}
              label={
                <Typography color="text-info" variant="body-semibold">
                  Show{' '}
                  {pluralize({
                    noun: 'product',
                    number: filteredProductCount,
                  })}
                </Typography>
              }
              size={isIpad ? 'small' : 'default'}
              onClick={onShowFilteredProducts}
              to={pathname.replace(/\/filters$/, '')}
            />
          </Flex>
        </Flex>
      </ModalHeader>
      <Modal.Content padding={false}>
        {filterGroups.map((group) => (
          <FilterSection
            key={`${group.key}-${group.label}`}
            filterGroup={group}
            onChange={onChange}
            onDeselect={onDeselect}
            searchState={searchState}
          />
        ))}
      </Modal.Content>
    </Modal>
  );
};
